import { lockBodyScroll } from "../utils/scrollLock";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabase";
import { getBlockedUserIds } from "../utils/blocks";
import LazyImage from "./LazyImage";
import { IoNotifications, IoNotificationsOutline, IoClose, IoWarningOutline, IoMegaphoneOutline } from "react-icons/io5";
import { PiHeartFill, PiChatCircleFill, PiUserPlusFill, PiDownloadSimpleFill, PiAtBold } from "react-icons/pi";
import { useFocusTrap } from "../utils/focusTrap";

const NOTIFICATIONS_PAGE = 10;

export default function NotificationBell({ user }) {
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const openingRef = useRef(null); // the in-flight openPanel() promise, or null
  const blockedIdsRef = useRef(new Set());
  // loadUnreadCount() can be in flight from more than one caller at once
  // (the mount-time getBlockedUserIds().then(...) chain, a realtime INSERT,
  // and markRead() all call it independently) — with no ordering guarantee
  // between them, a slower/older call finishing last could overwrite a
  // newer, more correct count with a stale one (e.g. the badge staying
  // stuck on an old number after everything visible was just marked read).
  // Only ever apply the result of whichever call started LAST.
  const unreadRequestIdRef = useRef(0);

  // Notifications come 10 at a time: the first page when the panel opens, then the
  // next one each time the end of the list scrolls into view.
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadedCountRef = useRef(0); // rows fetched so far (before hiding blocked people's)
  const loadingMoreRef = useRef(false);
  const bodyRef = useRef(null);
  const sentinelRef = useRef(null);
  const sheetRef = useRef(null);
  useFocusTrap(sheetRef, open);

  // A block only removes ITS OWN direction's follow row (see utils/blocks.js)
  // and going forward blocked users can't engage with each other at all —
  // but it doesn't delete notifications that were already created before
  // the block. Without filtering those out here too, someone you blocked
  // still shows up with their name, avatar and message in your own
  // notification panel, the same gap FollowListModal.jsx had.
  useEffect(() => {
    if (!user) return;
    getBlockedUserIds(user.id).then(ids => {
      blockedIdsRef.current = new Set(ids);
      loadUnreadCount();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Live updates: bump the badge the instant someone likes/comments/follows,
  // without needing a page refresh. Keyed on the id, not the whole user
  // object — a token refresh (~hourly) hands back a new session.user
  // reference for the same user, which would otherwise tear down and
  // recreate this channel needlessly.
  useEffect(() => {
    if (!user) return;

    // Re-fetches the true count rather than prev + 1 — mixing a relative
    // update here with the absolute one loadUnreadCount() does elsewhere
    // means whichever happens to resolve last wins and can stomp the other.
    // Keeping every update to this counter going through the same "ask the
    // server" path avoids that race entirely.
    const channel = supabase
      .channel(`notifications-${user.id}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` },
        () => loadUnreadCount()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Lock background scroll while the sheet is open, close on Escape
  useEffect(() => {
    if (!open) return;

    const unlockScroll = lockBodyScroll();

    function onKeyDown(e) {
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKeyDown);

    return () => {
      unlockScroll();
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  async function loadUnreadCount() {
    const requestId = ++unreadRequestIdRef.current;

    let query = supabase
      .from("notifications")
      .select("*", { count: "exact", head: true })
      .eq("user_id", user.id)
      .eq("read", false);

    const blocked = [...blockedIdsRef.current];
    if (blocked.length > 0) {
      // A plain .not("actor_id", "in", ...) becomes SQL `NOT (actor_id IN
      // (...))`, which evaluates to NULL — not TRUE — for a row whose
      // actor_id is NULL (broadcast announcements, sent via
      // admin_broadcast_announcement with no actor), so PostgREST drops
      // those rows from the count entirely rather than including them. That
      // silently undercounted the unread badge (sometimes to 0) for anyone
      // who'd blocked at least one person, even though the panel itself
      // (filtered client-side in fetchPage) showed them correctly.
      //
      // "warning" rows are also exempted — utils/notifications.js's
      // createNotification() deliberately skips the block check for that
      // type specifically so blocking the admin's account can't be used to
      // dodge being told about your own violation. Filtering them out here
      // (or in fetchPage below) would have silently undone that.
      query = query.or(`type.eq.warning,actor_id.is.null,actor_id.not.in.(${blocked.join(",")})`);
    }

    const { count } = await query;

    // A newer call already landed while this one was in flight — its
    // result is the stale one now, so don't let it clobber the fresher count.
    if (requestId !== unreadRequestIdRef.current) return;
    setUnreadCount(count || 0);
  }

  // One page of notifications, each with its actor and design attached.
  async function fetchPage(from) {
    const { data, error } = await supabase
      .from("notifications")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .range(from, from + NOTIFICATIONS_PAGE - 1);

    if (error) {
      console.log("LOAD NOTIFICATIONS ERROR:", error);
      return null;
    }

    // Not marked read and not counted either — if this person gets
    // unblocked later, their notification correctly reappears as unread,
    // since blocking never actually deleted it. "warning" rows are never
    // filtered by blocked actor — see the matching comment in
    // loadUnreadCount above.
    const fetched = data || [];
    const rows = fetched.filter(r => r.type === "warning" || !blockedIdsRef.current.has(r.actor_id));
    const actorIds = [...new Set(rows.map(r => r.actor_id))];
    const designIds = [...new Set(rows.map(r => r.design_id).filter(Boolean))];

    const [actorsRes, designsRes] = await Promise.all([
      actorIds.length > 0
        ? supabase.from("users").select("id, username, avatar_url").in("id", actorIds)
        : Promise.resolve({ data: [] }),
      designIds.length > 0
        ? supabase.from("designs").select("id, image_url").in("id", designIds)
        : Promise.resolve({ data: [] })
    ]);

    const actorsById = {};
    (actorsRes.data || []).forEach(a => (actorsById[a.id] = a));
    const designsById = {};
    (designsRes.data || []).forEach(d => (designsById[d.id] = d));

    return {
      fetchedCount: fetched.length,
      items: rows.map(r => ({
        ...r,
        actor: actorsById[r.actor_id],
        design: r.design_id ? designsById[r.design_id] : null
      }))
    };
  }

  async function markRead(items) {
    const unreadIds = items.filter(r => !r.read).map(r => r.id);
    if (unreadIds.length > 0) {
      await supabase.from("notifications").update({ read: true }).in("id", unreadIds);
    }
    // Re-fetch the true count from the server rather than adjusting the
    // client-side number by arithmetic. The realtime channel and this
    // REST call are two independent connections with no ordering
    // guarantee between them — a notification's own realtime "echo" can
    // arrive AFTER it's already been fetched and marked read right here,
    // and a naive prev-unreadIds.length subtract has no way to tell that
    // apart from a genuinely new one, leaving a phantom +1 stuck on the
    // badge until something else happens to correct it. A fresh COUNT
    // query is always right regardless of how those two races land.
    loadUnreadCount();
  }

  async function openPanel() {
    // Always opens right away — closing and reopening while the first
    // fetch/markRead is still in flight used to hit the guard below and
    // silently do nothing, making the bell look stuck until that original
    // call finished on its own.
    setOpen(true);
    setLoading(true);

    // A double-click/double-tap fast enough to land before the backdrop's
    // own click-blocking takes over — or a close+reopen while the first
    // call is still running — reuses that SAME in-flight request instead of
    // starting a second one, which would otherwise fetch and mark the same
    // page read twice.
    if (!openingRef.current) {
      openingRef.current = (async () => {
        try {
          // Refreshed on every open, not just once at mount — blocking
          // someone mid-session used to leave their notifications visible
          // in the panel until a reload.
          const ids = await getBlockedUserIds(user.id);
          blockedIdsRef.current = new Set(ids);

          const page = await fetchPage(0);
          if (!page) return;

          loadedCountRef.current = page.fetchedCount;
          setHasMore(page.fetchedCount === NOTIFICATIONS_PAGE);
          setNotifications(page.items);
          setLoading(false);

          await markRead(page.items);
        } finally {
          openingRef.current = null;
        }
      })();
    }

    await openingRef.current;
    setLoading(false);
  }

  async function loadMore() {
    if (loadingMoreRef.current || !hasMore) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);

    const page = await fetchPage(loadedCountRef.current);
    if (page) {
      loadedCountRef.current += page.fetchedCount;
      setHasMore(page.fetchedCount === NOTIFICATIONS_PAGE);
      setNotifications(prev => [...prev, ...page.items]);
      markRead(page.items);
    }

    loadingMoreRef.current = false;
    setLoadingMore(false);
  }

  // The next page loads when the end of the list scrolls into view.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!open || !el || !hasMore || loading) return undefined;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) loadMore();
      },
      { root: bodyRef.current, rootMargin: "0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, hasMore, loading, notifications.length]);

  function goTo(n) {
    setOpen(false);
    if (n.type === "follow") {
      // See who followed you, so you can follow back.
      navigate(`/u/${n.actor_id}`);
    } else if (n.type === "mention") {
      // A mention can be on anyone's post/comment, not necessarily yours.
      navigate(n.design_id ? `/d/${n.design_id}` : "/profile");
    } else if (n.type === "warning" || n.type === "announcement") {
      // Neither has a page of its own to go to — the message is the whole point.
      return;
    } else {
      // Likes/comments/downloads are always on YOUR OWN design — a
      // design_id is already fetched for every one of these (it's what
      // renders the row's own thumbnail), so go straight to that design
      // instead of dropping the person on their generic profile page.
      navigate(n.design_id ? `/d/${n.design_id}` : "/profile");
    }
  }

  // The person's name is shown bold, the rest of the sentence normal.
  function partsFor(n) {
    const name = n.actor?.username || "Someone";
    if (n.type === "like") return { name, rest: " liked your design" };
    if (n.type === "comment") return { name, rest: ` commented: ${n.comment_text || ""}` };
    if (n.type === "follow") return { name, rest: " started following you" };
    if (n.type === "download") return { name, rest: " downloaded your design" };
    if (n.type === "mention") {
      return { name, rest: ` mentioned you${n.comment_text ? `: ${n.comment_text}` : " in a caption"}` };
    }
    if (n.type === "warning") return { name: null, rest: n.comment_text || "You've received a warning from moderators." };
    if (n.type === "announcement") return { name: null, rest: n.comment_text || "" };
    return { name: null, rest: "" };
  }

  // White icon on a coloured dot, pinned to the corner of the avatar.
  function badgeFor(n) {
    const kinds = {
      like: [<PiHeartFill />, "var(--accent-danger-strong)"],
      comment: [<PiChatCircleFill />, "var(--accent-primary)"],
      follow: [<PiUserPlusFill />, "var(--accent-success)"],
      download: [<PiDownloadSimpleFill />, "var(--accent-gold)"],
      mention: [<PiAtBold />, "var(--accent-primary)"],
      warning: [<IoWarningOutline />, "var(--accent-danger)"],
      announcement: [<IoMegaphoneOutline />, "var(--accent-primary)"]
    };
    const kind = kinds[n.type];
    if (!kind) return null;
    return <span style={{ ...typeBadge, background: kind[1] }}>{kind[0]}</span>;
  }

  // One notification. `read` is what it was when the panel was opened (they are
  // all marked read right after), so it tells what is new this time.
  function renderRow(n) {
    const isImpersonal = n.type === "warning" || n.type === "announcement";
    const { name, rest } = partsFor(n);
    const isNew = !n.read;

    return (
      <div
        key={n.id}
        className="notif-row"
        style={{ ...row, ...(isNew ? rowNew : {}) }}
        onClick={() => goTo(n)}
      >
        <span style={avatarWrap}>
          <LazyImage
            // A warning's (and an announcement's) message is
            // deliberately impersonal ("from moderators"/the app
            // itself, no name) — showing the acting admin's own
            // face right next to it would undo that on sight,
            // even though the text hides it.
            src={isImpersonal ? "/default-avatar.svg" : n.actor?.avatar_url || "/default-avatar.svg"}
            alt={isImpersonal ? "GatherYou" : n.actor?.username || "user"}
            style={rowAvatar}
          />
          {badgeFor(n)}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={rowText}>
            {name && <strong style={rowName}>{name}</strong>}
            {rest}
          </p>
          <span style={rowMeta}>
            {timeAgo(n.created_at)}
            {isNew && <span style={newDot} aria-label="New" />}
          </span>
        </div>
        {n.design?.image_url && <LazyImage src={n.design.image_url} alt="" style={rowThumb} />}
      </div>
    );
  }

  const newOnes = notifications.filter(n => !n.read);
  const earlier = notifications.filter(n => n.read);

  if (!user) return null;

  return (
    <>
      <button onClick={openPanel} style={bellBtn} aria-label="Notifications">
        <IoNotifications />
        {unreadCount > 0 && (
          <span style={badge}>{unreadCount > 9 ? "9+" : unreadCount}</span>
        )}
      </button>

      {open &&
        createPortal(
          <div className="notif-backdrop" onClick={e => e.target === e.currentTarget && setOpen(false)}>
            <div className="notif-sheet" ref={sheetRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Notifications">
              <style>{`
                .notif-backdrop {
                  position: fixed;
                  inset: 0;
                  background: rgba(20, 14, 8, 0.55);
                  z-index: 1000;
                  display: flex;
                  align-items: flex-end;
                  justify-content: center;
                }
                .notif-sheet {
                  background: var(--bg-surface);
                  width: 100%;
                  max-height: 78vh;
                  border-radius: 18px 18px 0 0;
                  display: flex;
                  flex-direction: column;
                  box-shadow: 0 -4px 24px rgba(0,0,0,0.2);
                  animation: notif-slide-up 0.22s ease-out;
                }
                @keyframes notif-slide-up {
                  from { transform: translateY(100%); }
                  to { transform: translateY(0); }
                }
                .notif-handle {
                  width: 40px;
                  height: 4px;
                  border-radius: 2px;
                  background: var(--border-input);
                  margin: 10px auto 0;
                  flex-shrink: 0;
                }
                .notif-header {
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  position: relative;
                  padding: 12px 16px;
                  border-bottom: 1px solid var(--border-soft);
                  flex-shrink: 0;
                }
                .notif-row:active { background: var(--bg-soft); }
                .notif-close {
                  position: absolute;
                  right: 12px;
                  top: 50%;
                  transform: translateY(-50%);
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  width: 32px;
                  height: 32px;
                  background: var(--bg-soft);
                  border: none;
                  border-radius: 50%;
                  font-size: 18px;
                  line-height: 1;
                  cursor: pointer;
                  color: var(--text-body);
                  padding: 0;
                }
                .notif-body {
                  flex: 1;
                  overflow-y: auto;
                  padding: 6px 8px 12px;
                  -webkit-overflow-scrolling: touch;
                }
                @media (min-width: 641px) {
                  .notif-backdrop { align-items: center; }
                  .notif-sheet {
                    width: 92%;
                    max-width: 420px;
                    max-height: 78vh;
                    border-radius: 16px;
                    animation: notif-pop-in 0.18s ease-out;
                  }
                  .notif-handle { display: none; }
                  @keyframes notif-pop-in {
                    from { transform: scale(0.96); opacity: 0; }
                    to { transform: scale(1); opacity: 1; }
                  }
                }
              `}</style>

              <div className="notif-handle" />
              <div className="notif-header">
                <strong style={{ fontSize: 16, fontWeight: 800, color: "var(--text-heading)" }}>Notifications</strong>
                <button className="notif-close" onClick={() => setOpen(false)} aria-label="Close">
                  <IoClose />
                </button>
              </div>

              <div className="notif-body" ref={bodyRef}>
                {loading ? (
                  <p style={{ ...mutedText, textAlign: "center", padding: "28px 0" }}>Loading…</p>
                ) : notifications.length === 0 ? (
                  <div style={emptyState}>
                    <span style={emptyIcon} aria-hidden="true">
                      <IoNotificationsOutline size={24} />
                    </span>
                    <p style={emptyText}>No notifications yet.</p>
                  </div>
                ) : (
                  <>
                    {newOnes.length > 0 && (
                      <>
                        <div style={sectionLabel}>New</div>
                        {newOnes.map(renderRow)}
                      </>
                    )}
                    {earlier.length > 0 && (
                      <>
                        {newOnes.length > 0 && <div style={sectionLabel}>Earlier</div>}
                        {earlier.map(renderRow)}
                      </>
                    )}
                    {hasMore && <div ref={sentinelRef} style={{ height: 1 }} />}
                    {loadingMore && <p style={{ ...mutedText, textAlign: "center" }}>Loading more…</p>}
                  </>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

function timeAgo(dateString) {
  const seconds = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateString).toLocaleDateString();
}

/* ===== STYLES (Virsa palette, matches Comments/FollowListModal) ===== */

const bellBtn = {
  position: "relative",
  display: "flex",
  alignItems: "center",
  fontSize: 26,
  background: "none",
  border: "none",
  color: "var(--text-primary)",
  cursor: "pointer",
  padding: 0
};

const badge = {
  position: "absolute",
  top: -4,
  right: -6,
  minWidth: 16,
  height: 16,
  padding: "0 3px",
  borderRadius: 8,
  background: "var(--accent-danger-strong)",
  color: "var(--accent-contrast)",
  fontSize: 10,
  fontWeight: 700,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  lineHeight: 1
};

const row = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "10px 10px",
  borderRadius: 14,
  cursor: "pointer"
};

// A notification you haven't seen yet gets a soft tint and a dot.
const rowNew = { background: "var(--bg-soft)" };

const avatarWrap = { position: "relative", flexShrink: 0, display: "flex" };

const rowAvatar = {
  width: 46,
  height: 46,
  borderRadius: "50%",
  objectFit: "cover",
  border: "1px solid var(--border-soft)",
  flexShrink: 0
};

const typeBadge = {
  position: "absolute",
  right: -4,
  bottom: -2,
  width: 20,
  height: 20,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff",
  fontSize: 11,
  border: "2px solid var(--bg-surface)",
  boxSizing: "border-box"
};

const rowText = {
  margin: 0,
  fontSize: 14,
  color: "var(--text-heading)",
  lineHeight: 1.4,
  wordBreak: "break-word",
  // comment_text is capped at 500 chars (Comments.jsx), but even a few
  // hundred can stretch a notification row well past its normal height —
  // clamp to 3 lines with an ellipsis instead of letting it run on.
  display: "-webkit-box",
  WebkitLineClamp: 3,
  WebkitBoxOrient: "vertical",
  overflow: "hidden"
};

const rowName = { fontWeight: 700, color: "var(--text-primary)" };

const rowMeta = { display: "block", marginTop: 2, fontSize: 12, color: "var(--text-muted)" };

const newDot = {
  display: "inline-block",
  width: 7,
  height: 7,
  marginLeft: 7,
  borderRadius: "50%",
  background: "var(--accent-primary)",
  verticalAlign: 1
};

const rowThumb = {
  width: 48,
  height: 48,
  borderRadius: 10,
  objectFit: "cover",
  flexShrink: 0
};

const sectionLabel = {
  padding: "10px 10px 4px",
  fontSize: 12,
  fontWeight: 800,
  letterSpacing: 0.4,
  textTransform: "uppercase",
  color: "var(--text-muted)"
};

const emptyState = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 10,
  padding: "40px 20px"
};

const emptyIcon = {
  width: 52,
  height: 52,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "var(--bg-soft)",
  color: "var(--accent-primary)"
};

const emptyText = { margin: 0, fontSize: 13.5, color: "var(--text-muted)", textAlign: "center" };

const mutedText = { fontSize: 13, color: "var(--text-muted)", margin: "8px 0" };
